import * as React from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useAPIContext } from "components/providers/Api";
import { useUserContext } from "components/providers/User";
import { Col, Form, message, Card, Collapse, Icon } from "antd";
import { Tooltip } from "@lunchboxinc/utensils";
import { Elements, Fields, Fragments, Hocs, Templates } from "components";
import { Field, Formik } from "formik";
import { constants, schemas, utils } from "common";
import OptionsGroupTable from "./OptionsGroupTable";

import css from "../index.module.css";

const { OptionSchema } = schemas;
const { mapAxiosError } = utils;
const {
  Titles: { EditableTitle, SectionTitle },
  T,
  Button,
  Row,
} = Elements;
const {
  Tables: { LocationToggleTable },
  Permissions: { Permission },
  EntityToggle,
  Cards: { BlueCard },
  Condition: { Condition },
  Drawers: { ResDrawer },
} = Fragments;
const { OrderTypeAvailability, MediaSelection, LocationSelector } = Templates;
const { withPermissionField } = Hocs;
const { Panel } = Collapse;

const { permissions } = constants;
const {
  methods: { PUT },
  routes: { MENU_OPTIONS_ITEM },
} = permissions;

const updateOptionPermission = [{ method: PUT, url: MENU_OPTIONS_ITEM }];
const updateLocationAvailabilityPermission = [
  { method: PUT, url: MENU_OPTIONS_ITEM, field: "disabledLocations" },
];
const PermissionButton = withPermissionField(
  Button,
  [...updateOptionPermission, ...updateLocationAvailabilityPermission],
  true,
);

const withOptionPermission = (Component) =>
  withPermissionField(Component, updateOptionPermission);
const PermissionMediaButton = withOptionPermission(MediaSelection);
const PermissionInput = withOptionPermission(Fields.Input);
const PermissionCheckbox = withOptionPermission(Fields.Checkbox);
const PermissionOrderTypeAvailability = withOptionPermission(
  OrderTypeAvailability,
);
const PermissionSelect = withOptionPermission(Fields.Select);

const Option = ({ auditButton, locationId, locations, setName }) => {
  const { optionId } = useParams();
  const history = useHistory();
  const { pathname } = useLocation();
  const { MenuOptions } = useAPIContext();
  const { rgConfiguration } = useUserContext();
  const [isFetching, setIsFetching] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [optionData, setOptionData] = React.useState({});

  const fetchOption = async (optionId) => {
    try {
      const res = await MenuOptions.readOne(optionId, { location: locationId });
      setOptionData({ ...res.data });
      setName(res.data.name);
    } catch (e) {
      message.error(mapAxiosError(e));
    } finally {
      setIsFetching(false);
    }
  };

  const setLocations = (locationsRemaining = []) => {
    setOptionData({
      ...optionData,
      excludedLocations: [...locationsRemaining],
    });
  };

  const save = async (values, actions) => {
    setIsSaving(true);

    if (actions?.saveOptionGroup) {
      values.modifierType = optionData.modifierType;
    }

    if (values.excludedLocations) {
      values.excludedLocations = values.excludedLocations.map(
        (loc) => loc.id || loc,
      );
    }
    try {
      const res = await MenuOptions.update(optionId, values);
      message.success("Successfully saved");
      setOptionData(res.data);
    } catch (e) {
      message.error(mapAxiosError(e));
    } finally {
      setIsSaving(false);
      if (actions?.setSubmitting) actions.setSubmitting(false);
    }
  };

  const onSave = (values, actions) => {
    values.disabledLocations = values.disabledLocations.map((i) => i.id);
    values.priority = values.priority ? values.priority : null;

    values = {
      ...values,
      excludedLocations: values.excludedLocations?.map(
        (location) => location.id,
      ),
    };
    save(values, actions);
  };

  React.useEffect(() => {
    fetchOption(optionId);
  }, [optionId, locationId]);

  const {
    additionalDescriptions = [],
    complimentaryQuantity = null,
    defaultModifiers = [],
    disabledLocations = [],
    excludedLocations = [],
    externalId = "",
    images = [],
    items = [],
    isActive = false,
    isSingleSelectionOnly = false,
    max = 0,
    min = 0,
    modifierType = "",
    name = "",
    orderTypeAvailability = [],
    priority = 0,
    visibility = "NONE",
    isDeleted = false,
  } = optionData;

  const locationOptions = locations.map((i) => ({
    name: i.name,
    value: i.id,
  }));
  return (
    <>
      <BlueCard>
        <Row spacing={20} className={css.header}>
          <Col xs={24} sm={12} md={14}>
            <EditableTitle placeholder="Menu Option" name={name} save={save} />
          </Col>
          <Col xs={24} sm={12} md={10} className={css.entityToggle}>
            <Condition is={!min || min === 0}>
              <Permission
                requiredPermissions={{
                  method: PUT,
                  url: MENU_OPTIONS_ITEM,
                  field: "isActive",
                }}
                yes={() => (
                  <>
                    <EntityToggle
                      checked={isActive}
                      path={`/menu-options/${optionId}`}
                    />
                  </>
                )}
              />
            </Condition>
          </Col>
        </Row>
        <Row spacing={20}>
          <Formik
            enableReinitialize
            initialValues={{
              additionalDescriptions: additionalDescriptions[0],
              complimentaryQuantity,
              disabledLocations,
              images,
              isActive,
              isSingleSelectionOnly,
              max,
              min,
              modifierType,
              name,
              orderTypeAvailability,
              priority: priority ?? 0,
              visibility,
              excludedLocations,
            }}
            validationSchema={OptionSchema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={onSave}
            render={({ handleSubmit, values, setFieldValue, isSubmitting }) => (
              <Form onSubmit={handleSubmit}>
                <Col xs={24} md={12}>
                  <div className={css.entityDetailsContainer}>
                    <SectionTitle>Edit Details</SectionTitle>

                    <Row spacing={40}>
                      <Row spacing={10}>
                        <T label inline>
                          POS GUID:
                        </T>
                        <T copyable inline>
                          {externalId}
                        </T>
                      </Row>
                      <Row gutter={15} spacing={10}>
                        {rgConfiguration?.menuEntityEdit && (
                          <>
                            <Permission
                              requiredPermissions={[
                                {
                                  method: PUT,
                                  url: MENU_OPTIONS_ITEM,
                                  field: "min",
                                },
                              ]}
                              yes={() => (
                                <Col xs={24} sm={12}>
                                  <Field
                                    label="Min"
                                    name="min"
                                    size="small"
                                    type="number"
                                    disabled={isFetching || isSubmitting}
                                    component={PermissionInput}
                                  />
                                </Col>
                              )}
                            />
                            <Permission
                              requiredPermissions={[
                                {
                                  method: PUT,
                                  url: MENU_OPTIONS_ITEM,
                                  field: "max",
                                },
                              ]}
                              yes={() => (
                                <Col xs={24} sm={12}>
                                  <Field
                                    label="Max"
                                    name="max"
                                    size="small"
                                    type="number"
                                    disabled={isFetching || isSubmitting}
                                    component={PermissionInput}
                                  />
                                </Col>
                              )}
                            />
                          </>
                        )}

                        {!rgConfiguration?.menuEntityEdit && (
                          <>
                            <Col xs={24} sm={12}>
                              <T label>
                                Min: <strong>{min}</strong>
                              </T>
                            </Col>
                            <Col xs={24} sm={12}>
                              <T label>
                                Max: <strong>{max}</strong>
                              </T>
                            </Col>
                          </>
                        )}
                      </Row>

                      <Condition is={max > 1}>
                        <Row spacing={10}>
                          <Field
                            label="Complimentary Quantity"
                            name="complimentaryQuantity"
                            placeholder="0"
                            min={0}
                            max={max}
                            size="small"
                            type="number"
                            component={PermissionInput}
                          />
                        </Row>
                      </Condition>

                      <Row spacing={20}>
                        <Field
                          disabled={isFetching}
                          label="This option can be selected once (per item)"
                          checked={values.isSingleSelectionOnly}
                          component={PermissionCheckbox}
                          onChange={(e) => {
                            setFieldValue(
                              "isSingleSelectionOnly",
                              e.target.checked,
                            );
                          }}
                        />
                      </Row>

                      <Row spacing={12}>
                        <Field
                          label="Priority"
                          name="priority"
                          placeholder="0"
                          size="small"
                          type="number"
                          component={PermissionInput}
                        />
                      </Row>
                      {rgConfiguration?.menuEntityEdit && (
                        <Permission
                          requiredPermissions={[
                            {
                              method: PUT,
                              url: MENU_OPTIONS_ITEM,
                              field: "excludedLocations",
                            },
                          ]}
                          yes={() => (
                            <Row spacing={40}>
                              <T>Excluded Locations</T>
                              <ResDrawer
                                title="Excluded Locations"
                                placement="right"
                                width="50vw"
                                button={
                                  <Button>
                                    {values.excludedLocations.length}
                                    &nbsp; Locations
                                  </Button>
                                }
                              >
                                {(drawerProps) => (
                                  <LocationSelector
                                    loading={isFetching}
                                    locations={excludedLocations}
                                    setLocations={setLocations}
                                    addLocations={(locations) =>
                                      setFieldValue(
                                        "excludedLocations",
                                        locations,
                                      )
                                    }
                                    button={() => (
                                      <Button
                                        onClick={() => drawerProps.close()}
                                      >
                                        Close
                                      </Button>
                                    )}
                                  />
                                )}
                              </ResDrawer>
                            </Row>
                          )}
                        />
                      )}

                      <Row spacing={30}>
                        <Field
                          name="orderTypeAvailability"
                          disabled={isFetching}
                          component={PermissionOrderTypeAvailability}
                        />
                      </Row>

                      <Row spacing={12}>
                        <Field
                          label="Modifier Type"
                          name="modifierType"
                          width="70%"
                          options={[
                            { name: "Default", value: "" },
                            {
                              name: "Inline",
                              value: "inline",
                            },
                            { name: "Placement", value: "placement" },
                            { name: "Option", value: "option" },
                          ]}
                          component={PermissionSelect}
                        />
                      </Row>
                    </Row>

                    <Permission
                      requiredPermissions={[
                        {
                          method: PUT,
                          url: MENU_OPTIONS_ITEM,
                          field: "isDeleted",
                        },
                        {
                          method: PUT,
                          url: MENU_OPTIONS_ITEM,
                          field: "isActive",
                        },
                      ]}
                      matchOne
                      yes={() => (
                        <Row>
                          <Collapse bordered={false}>
                            <Panel header="Advanced Settings">
                              <Permission
                                requiredPermissions={{
                                  method: PUT,
                                  url: MENU_OPTIONS_ITEM,
                                  field: "isDeleted",
                                }}
                                yes={() => (
                                  <Row spacing={15}>
                                    <T>
                                      Delete
                                      <Tooltip
                                        key="syncInfo"
                                        placement="top"
                                        popupLabel="Soft deletes the option making it unable to be fetched or displayed. This can be reversed"
                                      >
                                        <Icon
                                          type="info-circle"
                                          style={{
                                            marginLeft: "5px",
                                            verticalAlign: "center",
                                          }}
                                        />
                                      </Tooltip>
                                    </T>
                                    <EntityToggle
                                      checked={isDeleted}
                                      path={`/menu-options/${optionId}`}
                                      checkedChildren="Deleted"
                                      unCheckedChildren="Not Deleted"
                                      field="isDeleted"
                                    />
                                  </Row>
                                )}
                              />
                            </Panel>
                          </Collapse>
                        </Row>
                      )}
                    />

                    <Row spacing={20}>
                      <Col>
                        <PermissionMediaButton
                          images={values.images}
                          onSelect={(newImageSrc) => {
                            setFieldValue("images", [
                              ...values.images,
                              newImageSrc,
                            ]);
                          }}
                          onClickDelete={(removeIndex) => {
                            setFieldValue(
                              "images",
                              values.images.filter(
                                (i, idx) => idx !== removeIndex,
                              ),
                            );
                          }}
                        />
                      </Col>
                      <Col>Only images under 8mb can be uploaded</Col>
                    </Row>

                    <Col xs={12} className={css.saveButton}>
                      <PermissionButton
                        width="90%"
                        htmlType="submit"
                        size="small"
                        disabled={isSubmitting}
                        loading={isSubmitting}
                        color="blue"
                      >
                        Save
                      </PermissionButton>
                    </Col>
                    <Col xs={12} className={css.saveButton}>
                      <Condition is={auditButton}>{auditButton}</Condition>
                    </Col>
                  </div>
                </Col>

                <Col xs={24} md={12}>
                  <Row spacing={15}>
                    <LocationToggleTable
                      height={350}
                      loading={isFetching}
                      onSelection={(disabledLocations) =>
                        save({
                          ...values,
                          ...disabledLocations,
                        })
                      }
                      excludedLocations={excludedLocations}
                      disabledLocations={values.disabledLocations}
                      locations={locations}
                      enableToggle={!min || min === 0}
                      setFieldValue={setFieldValue}
                      autosave
                    />
                  </Row>
                </Col>
              </Form>
            )}
          />
        </Row>
      </BlueCard>

      <Col xs={24} lg={12}>
        <Row spacing={40}>
          <Card loading={isFetching} bordered={false}>
            <OptionsGroupTable
              createItemFetch={() => this.fetch(optionId)}
              fetching={isFetching}
              locationPathname={pathname}
              initialDefaultModifiers={defaultModifiers}
              items={items}
              history={history}
              save={save}
              saving={isSaving}
              max={max}
              optionId={optionId}
            />
          </Card>
        </Row>
      </Col>
    </>
  );
};

export default Option;
